<template>
  <content-popup
    ref="popup"
    class="drawing-tool-config-popup"
    :visible="visible"
    :reference-element="referenceElement"
    :show-close-button="true"
    :show-cancel-button="false"
    :show-save-button="false"
    placement="right-start"
    size="small"
    @on-close="handleClose"
  >
    <template #content>
      <div class="drawing-tool-config">
        <!-- Color selection for Pen and Highlighter -->
        <div v-if="showColorPicker" class="picker-wrapper">
          <div class="label">{{ t("tinymce_plugin_drawing_modal_colour_label") }}</div>
          <div class="color-picker">
            <span
              v-for="color in colors"
              :key="color.value"
              :class="{ selected: isSelectedColor(color.value) }"
              :style="{ backgroundColor: color.value }"
              class="color-option"
              @click="handleColorSelect(color.value)"
            />
          </div>
        </div>

        <!-- Thickness selection -->
        <div
          v-if="showThicknessPicker"
          class="picker-wrapper"
          :class="{ 'eraser-active': props.selectedTool === DRAWING_TOOL.ERASER }"
        >
          <div class="label">{{ t("tinymce_plugin_drawing_modal_thickness_label") }}</div>
          <div class="thickness-picker">
            <div
              v-for="size in thicknessSizes"
              :key="size"
              :class="{ selected: selectedThickness === size }"
              class="thickness-option"
              @click="handleThicknessSelect(size)"
            >
              <div class="thickness-preview">
                <div
                  class="thickness-line"
                  :style="{
                    height: `${Math.min(size, 40)}px`,
                    backgroundColor: '#0B233F',
                  }"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </content-popup>
</template>

<script setup lang="ts">
import { computed } from "vue";
import ContentPopup from "@plugins/common/components/ContentPopup.vue";
import { DRAWING_TOOL } from "@plugins/drawing/drawing.constants";
import { ERASER_SIZE, PEN_HIGHLIGHTER_SIZE } from "@plugins/drawing/drawing.config.ts";
import { useI18n } from "vue-i18n";
import { ColorItem } from "@plugins/drawing/types.ts";

interface Props {
  visible: boolean;
  referenceElement: HTMLElement | null;
  selectedTool: string;
  selectedColor: string;
  selectedThickness: number;
  colors: ColorItem[];
}

const props = defineProps<Props>();

const emit = defineEmits<{
  onClose: [];
  onColorChange: [color: string];
  onThicknessChange: [thickness: number];
}>();

const { t } = useI18n();

const showColorPicker = computed(() => {
  return (
    props.selectedTool === DRAWING_TOOL.PEN ||
    props.selectedTool === DRAWING_TOOL.HIGHLIGHTER ||
    props.selectedTool === DRAWING_TOOL.TEXT_BOX
  );
});

const showThicknessPicker = computed(() => {
  return (
    props.selectedTool === DRAWING_TOOL.PEN ||
    props.selectedTool === DRAWING_TOOL.HIGHLIGHTER ||
    props.selectedTool === DRAWING_TOOL.ERASER
  );
});

const thicknessSizes = computed(() => {
  if (props.selectedTool === DRAWING_TOOL.ERASER) {
    return ERASER_SIZE;
  }
  return PEN_HIGHLIGHTER_SIZE;
});

const isSelectedColor = (color: string) => {
  return props.selectedColor === color;
};

const handleColorSelect = (color: string) => {
  emit("onColorChange", color);
};

const handleThicknessSelect = (thickness: number) => {
  emit("onThicknessChange", thickness);
};

const handleClose = () => {
  emit("onClose");
};
</script>

<style lang="scss">
@import "@styles/layout/breakpoint";
@import "@styles/themes/sls-theme/tokens/typo";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
.drawing-tool-config-popup {
  width: 330px;
  top: 10rem !important;
  left: 50% !important;
  transform: translateX(-50%) !important;

  @include media("tablet-min") {
    top: 3.5rem !important;
    left: 8rem !important;
    transform: unset !important;
  }
  .content-popup-content {
    margin-top: -$spacing-09;
  }
  .picker-wrapper {
    &:nth-of-type(2) {
      margin-top: $spacing-09;
    }
    &.eraser-active {
      .thickness-line {
        aspect-ratio: 1/1;
      }
      .thickness-preview {
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .thickness-line {
        outline: 1px solid;
        background-color: $ui-01 !important;
        border-radius: unset;
      }
    }
    > .label {
      @include carbon--type-style("productive-heading-02");
      margin-bottom: $spacing-05;
    }
  }
  .thickness-picker {
    display: flex;
    gap: $spacing-05;
    align-items: center;
  }

  .thickness-preview {
    width: inherit;
  }
  .thickness-option {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 3rem;
    width: 3rem;
    padding: $spacing-03;
    background-color: $ui-02;
    border-radius: 8px;
    &.selected {
      border: 2px solid $interactive-01;
    }
  }

  .thickness-line {
    border-radius: 20px;
  }
}
</style>
